import React from 'react';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Icon, IconSize } from '@shared/icons';
import { useTutorialStore } from '../../application/store/TutorialStore';
import { APP_CONFIG } from '../../config/app.config';
import DashboardCardHeader from '@features/dashboard/components/general/DashboardCardHeader';
import {
  TUTORIAL_CATALOG,
  type TutorialCatalogStep,
} from './constants/tutorialCatalog';

const DashboardTutorial: React.FC = () => {
  const navigate = useNavigate();
  const colors = APP_CONFIG.ui.theme.colors;
  // Subscribe to completedSteps (not the method) so progress updates re-render.
  const completedSteps = useTutorialStore((s) => s.completedSteps);
  const openOverlay = useTutorialStore((s) => s.openOverlay);
  const completedIds = new Set(completedSteps.map((step) => step.id));

  const completedCount = TUTORIAL_CATALOG.filter((step) => completedIds.has(step.id)).length;
  const progress = (completedCount / TUTORIAL_CATALOG.length) * 100;

  const handleStepClick = (step: TutorialCatalogStep) => {
    if (step.overlay) {
      openOverlay(step.overlay);
    }
    navigate(step.path);
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: colors.border,
        borderRadius: '16px',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: colors.border,
        }}
      >
        <DashboardCardHeader
          title="Set up your account"
          subtitle="Set up the basics to complete your profile"
        />

        <Box sx={{ px: 2, pb: 2 }}>
          <Box
            sx={{
              height: 4,
              borderRadius: '50px',
              bgcolor: 'rgba(0, 132, 255, 0.08)',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${progress}%`,
                bgcolor: colors.primary,
                borderRadius: '50px',
                transition: 'width 240ms ease',
              }}
            />
          </Box>
        </Box>
      </Box>

      {TUTORIAL_CATALOG.map((step, index) => {
        const completed = completedIds.has(step.id);

        return (
          <Stack
            key={step.id}
            direction="row"
            alignItems="center"
            gap={2}
            onClick={() => handleStepClick(step)}
            sx={{
              p: 2,
              cursor: 'pointer',
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: colors.backgroundTint },
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                fontSize: 14,
                fontWeight: 500,
                bgcolor: completed ? colors.primary : 'background.paper',
                color: completed ? colors.buttonContent : colors.primary,
                border: completed ? 'none' : `0.5px solid ${colors.border}`,
                flexShrink: 0,
              }}
            >
              {completed ? (
                <Icon icon="Check" size={IconSize.LARGE} color={colors.buttonContent} />
              ) : (
                index + 1
              )}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                color="text.primary"
                sx={{ textDecoration: completed ? 'line-through' : 'none' }}
              >
                {step.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {step.subtitle}
              </Typography>
            </Box>

            <Icon icon="ChevronRight" size={IconSize.MEDIUM} color={colors.secondaryText} />
          </Stack>
        );
      })}
    </Box>
  );
};

export default DashboardTutorial;
